// ============================================
// AFFECTATION DES DOSSIERS — qui peut recevoir un dossier
// ============================================
// Les listes d'affectation et de transfert partent de GET /admin/agents
// (personnel actif). On y retire en plus les comptes techniques, que le
// backend ne filtre pas tous.

import { UserRole, type User } from '../../types';

/**
 * Comptes techniques, jamais proposés à l'affectation (même liste que le
 * backend, services/comptes-techniques.ts) :
 * - l'assistant IA, auteur des réponses automatiques ;
 * - le standard téléphonique, auteur des appels enregistrés ;
 * - la boîte e-mail du SAV, auteur des e-mails d'expéditeurs sans compte.
 */
export const COMPTES_TECHNIQUES = [
  'ai-assistant@kly-groupe.com',
  'standard-telephonique@kly-groupe.com',
  'boite-sav@kly-groupe.com',
];

/** Ce que fait chaque compte technique, dit à l'écran Équipe. */
export const ROLE_COMPTE_TECHNIQUE: Record<string, string> = {
  'ai-assistant@kly-groupe.com': "Signe les réponses automatiques de l'assistant IA.",
  'standard-telephonique@kly-groupe.com': 'Signe les appels enregistrés par le standard.',
  'boite-sav@kly-groupe.com': 'Signe les e-mails reçus de personnes sans compte SAV.',
};

export const estCompteTechnique = (u: Pick<User, 'email'>): boolean =>
  COMPTES_TECHNIQUES.includes((u.email || '').trim().toLowerCase());

/** Personnel actif, hors comptes techniques. */
export const estAssignable = (u: User): boolean =>
  u.role !== UserRole.CUSTOMER && u.role !== 'CUSTOMER' && u.isActive !== false && !estCompteTechnique(u);

/** Agents et superviseurs assignables : ceux que l'auto-assignation sollicite. */
export const agentsPourAutoAssignation = (agents: User[]): User[] =>
  agents.filter(u => estAssignable(u) && (u.role === UserRole.AGENT || u.role === UserRole.SUPERVISOR));

/**
 * Choisit, pour chacun des `nombre` dossiers, l'agent le moins chargé.
 * La charge de départ est `activeTicketsCount` (renvoyé par /admin/agents,
 * 0 s'il manque) ; chaque affectation du lot l'augmente d'une unité. À
 * égalité, l'ordre de la liste départage. Renvoie une liste vide sans agent.
 */
export function repartirSurLeMoinsCharge(agents: User[], nombre: number): User[] {
  if (agents.length === 0 || nombre <= 0) return [];
  const charges = agents.map(a => a.activeTicketsCount ?? 0);
  const choix: User[] = [];
  for (let n = 0; n < nombre; n++) {
    let meilleur = 0;
    for (let i = 1; i < agents.length; i++) {
      if (charges[i] < charges[meilleur]) meilleur = i;
    }
    choix.push(agents[meilleur]);
    charges[meilleur]++;
  }
  return choix;
}
